// Simple auth client for username/password authentication
import { useState, useEffect, useCallback } from 'react';

// Determine API base URL - in production, use same origin; in dev, use localhost:3001
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_BASE = isLocalhost ? 'http://localhost:3001' : '';

export interface User {
  id: string;
  username: string;
  name: string;
}

interface SessionState {
  user: User | null;
  isPending: boolean;
  error: string | null;
}

// Global session state
let globalSession: SessionState = {
  user: null,
  isPending: true,
  error: null,
};

// Listeners for session changes
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

// Fetch current session from server
async function fetchSession(): Promise<User | null> {
  try {
    const response = await fetch(`${API_BASE}/api/auth/session`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.user || null;
  } catch (error) {
    console.error('Failed to fetch session:', error);
    return null;
  }
}

// Initialize session on app load
(async () => {
  globalSession.user = await fetchSession();
  globalSession.isPending = false;
  notifyListeners();
})();

// React hook to use session
export function useSession() {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const listener = () => forceUpdate({});
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    data: globalSession.user ? { user: globalSession.user } : null,
    user: globalSession.user,
    isPending: globalSession.isPending,
    error: globalSession.error,
  };
}

// Sign in with username and password
export async function signIn(username: string, password: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!data.success) {
      return { success: false, error: data.error || '登录失败' };
    }

    globalSession.user = data.user;
    notifyListeners();
    return { success: true };
  } catch (error) {
    console.error('Sign in error:', error);
    return { success: false, error: '网络错误，请重试' };
  }
}

// Sign out
export async function signOut(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Sign out error:', error);
  }

  globalSession.user = null;
  notifyListeners();
}

// Create auth client object for compatibility
export const authClient = {
  useSession,
  signIn,
  signOut,
};

export { useSession as useAuth };
